// PK-11: React surface for candleStore. Owns subscription lifecycle, the
// status machine, and the day-boundary fetchFormingBar trigger. Pure-ish:
// the only DOM-touching path is the optional onFormingBarUpdate callback,
// which the consumer uses to apply the corrected bar to its LWC refs.
//
// Per-tick OHLC.update (price tracking) stays in the consumer because it
// touches LWC refs directly. The hook subscribes to ibkrStream only to
// detect day-boundary transitions and trigger fetchFormingBar; ibkrStream
// dedups WebSocket connections per symbol so a second listener is cheap.

import { useChartRequestOwner } from './useChartRequestOwner.js'
import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import {
  fetchBars,
  fetchCanonicalBarsBefore,
  fetchCanonicalBarsForDuration,
  fetchFormingBar,
  toCandles,
  aggregate65min,
  IBKR_TF,
  canonicalBarsReadEnabled,
  canonicalBarsStrictModeEnabled,
} from '../lib/candleStore'
import { utcToChartTimestamp, floorToBarPeriod } from '../lib/timezone'
import { isOCCSymbol, parseOCC } from '../lib/occParser'
import { ibkrStream } from '../lib/ibkrStream'
import { getMarketSession } from '../lib/marketSession'
import { getTickInstant } from '../lib/chart/getTickInstant.js'
import { fetchIbkrChartSnapshot } from '../lib/marketData/ibkrChartAdapter.js'
import { reportMarketDataStatus } from '../lib/marketDataStatusEvents'

// Bound the 5-min source feed before aggregating to 65-min. This leaves room
// for chunked scrollback without letting stale multi-year cache rows overwhelm
// Lightweight Charts.
const MAX_5MIN_BARS = 12000
const NATIVE_CANONICAL_65MIN_TF = Object.freeze({
  barSize: '65 mins',
  duration: '1 Y',
  scrollbackChunkDays: 120,
})
const VALID_MARKET_DATA_SESSIONS = new Set(['regular', 'all'])

function normalizeMarketSession(session) {
  return VALID_MARKET_DATA_SESSIONS.has(session) ? session : 'regular'
}

function buildRequestKey({
  symbol,
  timeframe,
  fetchDuration,
  timezone,
  marketSession,
  optExpiry,
  optStrike,
  optRight,
  optUnderlying,
  optConid,
  optExchange,
  optCurrency,
  optMultiplier,
  candleAuthority,
}) {
  return JSON.stringify({
    symbol: symbol || '',
    timeframe: timeframe || '',
    fetchDuration: fetchDuration || '',
    timezone: timezone || '',
    marketSession: normalizeMarketSession(marketSession),
    optExpiry: optExpiry || '',
    optStrike: optStrike || '',
    optRight: optRight || '',
    optUnderlying: optUnderlying || '',
    optConid: optConid || '',
    optExchange: optExchange || '',
    optCurrency: optCurrency || '',
    optMultiplier: optMultiplier || '',
    candleAuthority: candleAuthority || 'default',
  })
}

function rawBarTimeToEpochSeconds(rawTime) {
  const ts = typeof rawTime === 'string' ? rawTime : String(rawTime ?? '')
  const intraday = ts.match(/^(\d{4})(\d{2})(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/)
  if (intraday) {
    const [, y, mo, d, h, mi, s] = intraday
    const ref = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), 16, 0, 0))
    const etHour = Number(new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      hour12: false,
    }).formatToParts(ref).find(p => p.type === 'hour')?.value)
    const offset = 16 - etHour
    return Math.floor(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h) + offset, Number(mi), Number(s)) / 1000)
  }
  if (typeof rawTime === 'number' && Number.isFinite(rawTime)) return rawTime
  if (/^\d+$/.test(ts) && ts.length !== 8) return Number(ts)
  if (/^\d{8}$/.test(ts)) {
    return Math.floor(Date.UTC(Number(ts.slice(0, 4)), Number(ts.slice(4, 6)) - 1, Number(ts.slice(6, 8))) / 1000)
  }
  return null
}

function dedupeRawBars(bars) {
  const byTime = new Map()
  for (const bar of bars || []) {
    if (bar?.time != null) byTime.set(bar.time, bar)
  }
  return [...byTime.values()].sort((a, b) => {
    const ax = rawBarTimeToEpochSeconds(a.time) ?? 0
    const bx = rawBarTimeToEpochSeconds(b.time) ?? 0
    return ax - bx
  })
}

function convertRawBarsForTimeframe(rawBars, tf, timezone) {
  let converted = toCandles(rawBars, timezone)
  if (tf.aggregate) {
    if (converted.length > MAX_5MIN_BARS) converted = converted.slice(-MAX_5MIN_BARS)
    converted = aggregate65min(converted)
  }
  return converted
}

function nativeCanonicalTimeframeFor(timeframe, optContract) {
  if (timeframe !== '65min' || optContract || !canonicalBarsReadEnabled()) return null
  return NATIVE_CANONICAL_65MIN_TF
}

// Pure factory: builds the per-tick trigger handler used by the
// day-boundary effect. Extracted so the retry-on-null + in-flight dedupe +
// monotonic-marker logic can be unit-tested without React or a DOM. The
// hook calls this INSIDE its effect, so each effect run gets a fresh
// closure over `lastCorrectedBarTime` / `inFlightBarTime` and a fresh
// reference to the cancellation flag.
//
// The intentional timeframe filter keeps weekly out. 65min only participates
// when the native canonical chart path is enabled; the legacy IBKR short
// fetch still cannot produce a native 65-minute bar.
export function createFormingBarTrigger({
  symbol,
  timeframe,
  timezone,
  initialLastBarTime,
  fetchFormingBar,
  onCorrected,
  getCancelled,
  getMarketSession,
  floorToBarPeriod,
  getTickInstant,
}) {
  let lastCorrectedBarTime = initialLastBarTime
  let inFlightBarTime = null

  return function handleTick(tick) {
    if (getCancelled()) return
    if (tick.field !== 'last' || tick.value <= 0) return
    if (tick.source === 'ibkr-snapshot') return
    const canonical65min = timeframe === '65min' && canonicalBarsReadEnabled()
    if (
      timeframe !== 'daily'
      && timeframe !== '5min'
      && timeframe !== '15min'
      && timeframe !== '30min'
      && !canonical65min
    ) return

    // MD-00: bar identity derives from the tick's real-instant via the
    // canonical getTickInstant (eventTs > ts, no Date.now() fallback). A
    // null instant drops the tick — preserves the live-bar-continuity
    // contract that bad ticks never fabricate from the local clock.
    const instant = getTickInstant(tick)
    if (!instant) return
    if (getMarketSession(instant) !== 'regular') return
    const barTime = floorToBarPeriod(instant, timeframe, timezone)
    if (barTime === 0) return
    if (barTime === lastCorrectedBarTime) return
    if (barTime === inFlightBarTime) return

    inFlightBarTime = barTime
    fetchFormingBar(symbol, timeframe, timezone, barTime).then(corrected => {
      inFlightBarTime = null
      if (getCancelled()) return
      if (!corrected) return
      // Marker only advances forward. A late T-1 correction arriving after
      // T has already been corrected is still passed to onCorrected (the
      // consumer's merge decides what to do with a stale-bar correction),
      // but lastCorrectedBarTime does not regress.
      if (lastCorrectedBarTime === null || barTime > lastCorrectedBarTime) {
        lastCorrectedBarTime = barTime
      }
      onCorrected(corrected)
    }).catch(() => { inFlightBarTime = null })
  }
}

export function useCandleStore({
  symbol,
  timeframe,
  optContract = null,
  fetchDuration,
  timezone,
  marketSession = 'regular',
  onFormingBarUpdate,
  parityMode = false,
  candleAuthority = 'default',
  enabled = true,
}) {
  const [candles, setCandles] = useState([])
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState(null)
  const [candlesKey, setCandlesKey] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [streamUrl, setStreamUrl] = useState(null)
  const rawBarsRef = useRef([])
  const rawBarsTimeframeRef = useRef(null)
  const loadingMoreRef = useRef(false)
  const activeRequestKeyRef = useRef(null)
  const requestControllerRef = useRef(null)
  const requestOwner = useChartRequestOwner()

  // Latest callback ref so the tick subscription always sees the freshest
  // closure without re-subscribing on every render.
  const onUpdateRef = useRef(onFormingBarUpdate)
  useEffect(() => { onUpdateRef.current = onFormingBarUpdate }, [onFormingBarUpdate])

  const retry = useCallback(() => setReloadKey(k => k + 1), [])

  // Stable identity for option contract so the deps array doesn't churn.
  const optExpiry = optContract?.expiry ?? null
  const optStrike = optContract?.strike ?? null
  const optRight = optContract?.right ?? null
  const optUnderlying = optContract?.underlying ?? null
  const optConid = optContract?.conid ?? null
  const optExchange = optContract?.exchange ?? null
  const optCurrency = optContract?.currency ?? null
  const optMultiplier = optContract?.multiplier ?? null
  const runtimeContract = useMemo(() => optExpiry ? {
    secType: 'OPT',
    expiry: optExpiry,
    strike: optStrike,
    right: optRight,
    exchange: optExchange || 'SMART',
    currency: optCurrency || 'USD',
    ...(optConid == null ? {} : { conid: optConid }),
    ...(optMultiplier == null ? {} : { multiplier: optMultiplier }),
  } : null, [optConid, optCurrency, optExchange, optExpiry, optMultiplier, optRight, optStrike])
  const normalizedMarketSession = normalizeMarketSession(marketSession)
  const ibkrRuntime = candleAuthority === 'ibkr-runtime'
  const requestKey = useMemo(() => buildRequestKey({
    symbol,
    timeframe: parityMode ? `${timeframe}:parity` : timeframe,
    fetchDuration,
    timezone,
    marketSession: normalizedMarketSession,
    optExpiry,
    optStrike,
    optRight,
    optUnderlying,
    optConid,
    optExchange,
    optCurrency,
    optMultiplier,
    candleAuthority,
  }), [symbol, timeframe, fetchDuration, timezone, normalizedMarketSession, parityMode, optExpiry, optStrike, optRight, optUnderlying, optConid, optExchange, optCurrency, optMultiplier, candleAuthority])
  activeRequestKeyRef.current = requestKey

  useEffect(() => () => { activeRequestKeyRef.current = null }, [])

  // ── Fetch / transform ────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !symbol) return
    let cancelled = false
    const controller = new AbortController()
    requestControllerRef.current = controller
    requestOwner.current.requestGeneration += 1
    controller.requestOwner = { ...requestOwner.current }
    const loadKey = requestKey
    rawBarsRef.current = []
    rawBarsTimeframeRef.current = null
    loadingMoreRef.current = false

    async function load() {
      setStatus('loading')
      setError(null)
      setStreamUrl(null)
      try {
        if (ibkrRuntime) {
          const snapshot = await fetchIbkrChartSnapshot({
            symbol: optUnderlying || symbol,
            timeframe,
            session: normalizedMarketSession,
            ...(runtimeContract ? { contract: runtimeContract } : {}),
          }, { signal: controller.signal, ...controller.requestOwner })
          if (cancelled) return
          const tf = IBKR_TF[timeframe]
          const converted = convertRawBarsForTimeframe(snapshot.bars, tf, timezone)
          reportMarketDataStatus(converted.length > 0
            ? { source: 'chart', requestKey: loadKey, operational: true, state: 'ready', symbol, message: `${symbol} chart data loaded successfully.` }
            : {
                operational: false,
                state: 'empty',
                source: 'chart', requestKey: loadKey, symbol,
            request: { symbol: optUnderlying || symbol, timeframe, session: normalizedMarketSession, ...(runtimeContract ? { contract: runtimeContract } : {}) },
                message: `${symbol} did not receive chart data for this timeframe.`,
              })
          rawBarsRef.current = snapshot.bars
          rawBarsTimeframeRef.current = tf
          setCandles(converted)
          setStreamUrl(snapshot.streamUrl)
          setCandlesKey(loadKey)
          setStatus('ready')
          return
        }
        const tf = IBKR_TF[timeframe] || IBKR_TF['daily']
        const opt = optExpiry ? { underlying: optUnderlying, expiry: optExpiry, strike: optStrike, right: optRight } : null
        const nativeCanonicalTf = nativeCanonicalTimeframeFor(timeframe, opt)
        let raw
        let conversionTf = tf
        if (nativeCanonicalTf) {
          try {
            raw = await fetchCanonicalBarsForDuration(
              symbol,
              nativeCanonicalTf.barSize,
              fetchDuration || nativeCanonicalTf.duration,
              { session: normalizedMarketSession, includeCurrent: !parityMode },
            )
            conversionTf = nativeCanonicalTf
          } catch (err) {
            if (canonicalBarsStrictModeEnabled()) throw err
            raw = await fetchBars(symbol, tf.barSize, fetchDuration || tf.duration, opt, {
              session: normalizedMarketSession,
              parityMode,
            })
          }
        } else {
          raw = await fetchBars(symbol, tf.barSize, fetchDuration || tf.duration, opt, {
            session: normalizedMarketSession,
            parityMode,
          })
        }
        if (cancelled) return
        rawBarsRef.current = raw
        rawBarsTimeframeRef.current = conversionTf
        const converted = convertRawBarsForTimeframe(raw, conversionTf, timezone)
        if (cancelled) return
        setCandles(converted)
        setCandlesKey(loadKey)
        setStatus('ready')
      } catch (err) {
        if (cancelled) return
        if (ibkrRuntime) {
          reportMarketDataStatus({
            operational: false,
            state: err?.code === 'CHART_EMPTY' ? 'empty' : err?.code === 'CHART_AUTH' ? 'auth-error' : 'chart-error',
            source: 'chart', requestKey: loadKey, symbol,
            request: { symbol: optUnderlying || symbol, timeframe, session: normalizedMarketSession, ...(runtimeContract ? { contract: runtimeContract } : {}) },
            message: err?.code === 'CHART_EMPTY' ? `${symbol}: IBKR returned no bars for this timeframe.` : err?.code === 'CHART_AUTH' ? 'Your Erevis sign-in expired. Sign in again to load chart data.' : `${symbol} chart history could not load. Retry the chart; a connected Gateway does not confirm chart delivery.`,
          })
        }
        setError(err)
        setStatus('error')
      }
    }

    load()
    return () => {
      cancelled = true
      controller.abort()
      if (requestControllerRef.current === controller) requestControllerRef.current = null
    }
  }, [symbol, timeframe, fetchDuration, timezone, normalizedMarketSession, parityMode, ibkrRuntime, enabled, reloadKey, optUnderlying, optExpiry, optStrike, optRight, requestKey, runtimeContract])

  const reconcileStream = useCallback(async ({ isCurrent = () => true } = {}) => {
    if (!enabled || !ibkrRuntime || !symbol) {
      throw new Error('IBKR chart stream reconciliation is unavailable')
    }
    const reconcileKey = requestKey
    const controller = requestControllerRef.current
    if (!controller || controller.signal.aborted || activeRequestKeyRef.current !== reconcileKey) {
      throw new Error('IBKR chart subscription was superseded')
    }
    const snapshot = await fetchIbkrChartSnapshot({
      symbol: optUnderlying || symbol,
      timeframe,
      session: normalizedMarketSession,
      ...(runtimeContract ? { contract: runtimeContract } : {}),
    }, { signal: controller.signal, ...controller.requestOwner })
    if (controller.signal.aborted || !isCurrent() || activeRequestKeyRef.current !== reconcileKey) {
      throw new Error('IBKR chart subscription was superseded')
    }
    const tf = IBKR_TF[timeframe]
    const converted = convertRawBarsForTimeframe(snapshot.bars, tf, timezone)
    if (!converted.length) throw new Error('No IBKR chart data')
    rawBarsRef.current = snapshot.bars
    rawBarsTimeframeRef.current = tf
    setCandles(converted)
    setStreamUrl(snapshot.streamUrl)
    setCandlesKey(reconcileKey)
    setError(null)
    setStatus('ready')
    reportMarketDataStatus({ source: 'chart', requestKey: reconcileKey, operational: true, state: 'ready', symbol, message: `${symbol} chart data loaded successfully.` })
    return snapshot
  }, [enabled, ibkrRuntime, symbol, optUnderlying, requestKey, timeframe, normalizedMarketSession, timezone, runtimeContract])

  const loadMoreBefore = useCallback(async () => {
    if (!enabled || !symbol || (!ibkrRuntime && optExpiry) || loadingMoreRef.current) return 0
    const controller = requestControllerRef.current
    const loadKey = requestKey
    if (!controller || controller.signal.aborted || activeRequestKeyRef.current !== loadKey) return 0
    const tf = IBKR_TF[timeframe] || IBKR_TF['daily']
    const rawTf = rawBarsTimeframeRef.current || nativeCanonicalTimeframeFor(timeframe, null) || tf
    const earliest = rawBarsRef.current?.[0]?.time
    const before = rawBarTimeToEpochSeconds(earliest)
    if (!before) return 0

    loadingMoreRef.current = true
    try {
      const older = ibkrRuntime
        ? (await fetchIbkrChartSnapshot({
            symbol: optUnderlying || symbol,
            timeframe,
            session: normalizedMarketSession,
            before,
            ...(runtimeContract ? { contract: runtimeContract } : {}),
          }, { signal: controller.signal, ...controller.requestOwner })).bars
        : await fetchCanonicalBarsBefore(
          symbol,
          rawTf.barSize,
          before,
          { chunkDays: rawTf.scrollbackChunkDays, session: normalizedMarketSession },
        )
      if (controller.signal.aborted || activeRequestKeyRef.current !== loadKey || !older?.length) return 0
      const nextRaw = dedupeRawBars([...older, ...rawBarsRef.current])
      const priorConverted = convertRawBarsForTimeframe(rawBarsRef.current, rawTf, timezone)
      const nextConverted = convertRawBarsForTimeframe(nextRaw, rawTf, timezone)
      const added = Math.max(0, nextConverted.length - priorConverted.length)
      if (!added) return 0
      rawBarsRef.current = nextRaw
      setCandles(nextConverted)
      return added
    } catch (err) {
      if (controller.signal.aborted || activeRequestKeyRef.current !== loadKey) return 0
      console.warn('[useCandleStore] chart scrollback unavailable', {
        symbol,
        timeframe,
        error: err?.message || String(err),
      })
      return 0
    } finally {
      if (requestControllerRef.current === controller) loadingMoreRef.current = false
    }
  }, [enabled, symbol, optUnderlying, optExpiry, timeframe, timezone, normalizedMarketSession, ibkrRuntime, runtimeContract, requestKey])

  // ── Legacy/options day-boundary forming-bar trigger ──────────────────────
  // Only subscribes once we have an initial candle set so we can seed the
  // last bar's timestamp. Per-tick OHLC.update is the consumer's job — this
  // effect ONLY watches for barTime to advance to a new bar, then asks the
  // store to refetch IBKR's full OHLC for the forming bar.
  useEffect(() => {
    if (!enabled || !symbol) return
    if (ibkrRuntime) return
    if (parityMode) return
    if (status !== 'ready' || !candles.length) return
    if (timeframe === 'weekly') return
    // EXT currently reloads historical/all-session bars only; live forming
    // updates wait for the runtime WebSocket fanout to serve extended hours.
    if (normalizedMarketSession !== 'regular') return

    let cancelled = false
    let unsub = null

    const isOpt = isOCCSymbol(symbol)
    if (!isOpt && canonicalBarsReadEnabled()) return
    const occ = isOpt ? parseOCC(symbol) : null
    const streamSymbol = occ ? occ.underlying : symbol
    const streamParams = occ
      ? { secType: 'OPT', expiry: occ.expiry, strike: String(occ.strike), right: occ.right }
      : {}

    const handleTick = createFormingBarTrigger({
      symbol,
      timeframe,
      timezone,
      initialLastBarTime: candles[candles.length - 1].time,
      fetchFormingBar: (...args) => fetchFormingBar(...args, { session: normalizedMarketSession }),
      onCorrected: (corrected) => {
        const cb = onUpdateRef.current
        if (cb) cb(corrected)
      },
      getCancelled: () => cancelled,
      getMarketSession,
      floorToBarPeriod,
      getTickInstant,
    })

    ibkrStream.subscribe(streamSymbol, handleTick, streamParams).then(u => {
      if (cancelled) {
        if (u) u()
        return
      }
      unsub = u
    }).catch(() => {})

    return () => {
      cancelled = true
      if (unsub) unsub()
    }
  }, [symbol, timeframe, timezone, normalizedMarketSession, status, candles, parityMode, ibkrRuntime, enabled])

  return { candles, status, error, retry, requestKey, candlesKey, streamUrl, reconcileStream, loadMoreBefore }
}
